import { formatChartFilterLabel } from "./chart-filter-label";
import {
  filterDates,
  formatLongToYmd,
  formatLongWithDateType,
  getMonthInLong,
} from "./date";
import { ChartType, DashType, DateTypes, UIConfig } from "./configs";
import {
  getSidoColor,
  getPointRange,
  formatYAxisLabel,
  roundToTwoDecimal,
  sortBySecondItemDe,
  sortBySecondItemIn,
  getSystemColor,
} from "./util";
import {
  getBarOption,
  getChartLineOption,
  getColumnOption,
  getGeoMapOption,
  getHeatmapOption,
  getTilemapOption,
} from "./data-config";
import {
  filterDatetimeData,
  filterGunguIndexs,
  filterIndexsById,
  isIndexInIds,
} from "./data-filter";
import { getRegionTilemapConfig } from "./region-config";
import { chartColors } from "./design";

const getYAxisLabel = (enabled = true) => {
  return {
    enabled,
    formatter: function () {
      return formatYAxisLabel(this.value, 0);
    },
    style: {
      color: chartColors[0],
    },
  };
};

const getDatetimeXaxis = (dateType, xTickDays = null) => {
  const tickIntervalMs = xTickDays
    ? 24 * 3600 * 1000 * xTickDays
    : 24 * 3600 * 1000 * 180;

  const axis = {
    events: {
      setExtremes: null,
    },
    type: "datetime",
    tickInterval: tickIntervalMs,
    labels: {
      formatter: function () {
        return formatLongWithDateType(this.value, dateType);
      },
    },
  };

  // xTickDays가 명시되지 않으면 기존 tickPositioner 사용 (중복 라벨 제거)
  // xTickDays가 명시되면 tickInterval만으로 제어 (tickPositioner가 덮어쓰지 않도록)
  if (!xTickDays) {
    axis.tickPositioner = function () {
      const ticks = this.tickPositions;
      const positions = [ticks[0]];
      for (let i = 1; i < ticks.length; i++) {
        if (
          formatLongWithDateType(ticks[i], dateType) !==
          formatLongWithDateType(ticks[i - 1], dateType)
        ) {
          positions.push(ticks[i]);
        }
      }
      return positions;
    };
  }

  return axis;
};

const getScatterValue = (
  name,
  color,
  data,
  yAxis = 0,
  label = false,
  markerSize = 2,
  markerSymbol = "circle",
  showInLegend = true
) => {
  return {
    type: "scatter",
    name,
    yAxis,
    color,
    data,
    showInLegend,
    marker: {
      enabled: true,
      radius: markerSize,
      symbol: markerSymbol, // circle, square, triangle, etc.
      lineWidth: 0, // 기본은 테두리 없음
      states: {
        hover: {
          enabled: true,
          lineWidthPlus: 1, // 마우스 오버 시 테두리 두께
          lineColor: "#000000", // 테두리 색 (기본: 흰색)
          radiusPlus: 2, // hover 시 마커 크기 증가
        },
      },
    },
    dataLabels: {
      enabled: false,
      formatter: function () {
        return this.series.name;
      },
      style: {
        color: color,
      },
    },
  };
};

const getLineValue = (
  name,
  color,
  data,
  yAxis = 0,
  label = true,
  dashStyle = DashType.SOLID,
  lineWidth = 2,
  connectNulls = true,
  showMarker = false,
  markerSize = 2,
  showInLegend = true,
  stack = undefined,
  stacking = undefined,
  showValue = false
) => {
  return {
    type: "spline",
    name,
    yAxis,
    color,
    dashStyle,
    lineWidth,
    data,
    connectNulls,
    showInLegend,
    stack,
    stacking,
    states: {
      hover: {
        enabled: false,
      },
    },
    marker: {
      enabled: showMarker || showValue,
      radius: showMarker ? markerSize : showValue ? 3 : 0,
      symbol: "circle",
    },
    dataLabels: {
      enabled: label || showValue,
      allowOverlap: showValue,
      position: showValue ? "top" : "right",
      style: {
        // 라인과 라벨이 동일 색이면 묻혀 보이므로 흰 외곽선으로 가독성 확보
        textOutline: "2px #ffffff",
        fontSize: showValue ? "10px" : undefined,
      },
      x: showValue ? 0 : 50,
      y: showValue ? -8 : 0,
      formatter: function () {
        const seriesColor = this.series.color || color || "#000000";
        if (showValue) {
          // 점수 값 라벨: 라인 색과 구분되는 차분한 진한 회색 (Blue Grey 700)
          const val = this.y != null ? Math.round(this.y) : "";
          return `<span style="color:#455A64;font-size:10px">${val}</span>`;
        }
        // 기본: 마지막 포인트에 series.name — 어느 라인인지 식별 위해 series 색 유지
        if (this.point === this.series.data[this.series.data.length - 1]) {
          return `<span style="color:${seriesColor}">${this.series.options.custom?.filterLabel ?? this.series.name}</span>`;
        }
        return null;
      },
    },
  };
};

const getAreaValue = (
  name,
  color,
  data,
  dateType,
  yAxis = 0,
  enable = false,
  lastLabel = false,
  showInLegend = true,
  stack = undefined,
  stacking = undefined,
  showValue = false
) => {
  return {
    name,
    type: "area",
    color,
    yAxis,
    data,
    pointRange: getPointRange(dateType),
    pointPadding: 0,
    pointPlacement: 0,
    groupPadding: 0,
    showInLegend,
    stack,
    stacking,
    states: {
      hover: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: enable || showValue,
      allowOverlap: showValue,
      color,
      style: {
        textOutline: color,
        fontSize: showValue ? "10px" : undefined,
      },
      formatter: function () {
        const seriesColor = this.series.color || color || "#000000";
        this.color = seriesColor;
        if (showValue) {
          return `<span style="color:${seriesColor};font-size:10px">${Math.round(this.y)}</span>`;
        }
        if (lastLabel) {
          if (this.point === this.series.data[this.series.data.length - 1]) {
            return `<span style="color:${seriesColor}">${this.series.options.custom?.filterLabel ?? this.series.name}</span>`;
          }
        } else {
          return roundToTwoDecimal(this.y);
        }
      },
    },
  };
};

const getColumnValue = (
  name,
  color,
  data,
  dateType,
  yAxis = 0,
  enable = false,
  lastLabel = false,
  showInLegend = true,
  stack = undefined,
  stacking = undefined,
  showValue = false
) => {
  return {
    name,
    type: "column",
    color,
    yAxis,
    data,
    pointRange: getPointRange(dateType),
    pointPadding: 0,
    pointPlacement: 0,
    groupPadding: 0,
    borderWidth: 0,
    showInLegend,
    stack,
    stacking,
    states: {
      hover: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: enable || showValue,
      allowOverlap: showValue,
      borderColor: color,
      style: {
        textOutline: color,
        fontSize: showValue ? "10px" : undefined,
      },
      formatter: function () {
        const seriesColor = this.series.color || color || "#000000";
        this.color = seriesColor;
        if (showValue) {
          return `<span style="color:${seriesColor};font-size:10px">${Math.round(this.y)}</span>`;
        }
        if (lastLabel) {
          if (this.point === this.series.data[this.series.data.length - 1]) {
            return `<span style="color:${seriesColor}">${this.series.options.custom?.filterLabel ?? this.series.name}</span>`;
          }
        } else {
          return roundToTwoDecimal(this.y);
        }
      },
    },
  };
};

export class ChartBuilder {
  xAxis;
  yAxis;
  yValues;
  chartName;
  dateType;
  height;
  constructor(chartName, dateType = DateTypes.WEEKLY, height = undefined, options = {}) {
    this.chartName = chartName;
    this.yAxis = [];
    this.xAxis = getDatetimeXaxis(dateType, options.xTickDays ?? null);
    this.yValues = [];
    this.height = height;
  }

  getCurrentYAxis = () => {
    return this.yAxis.length - 1;
  };

  addYAxisPlotLine = (yIndex, plotLine) => {
    this.yAxis[yIndex].plotLines.push(plotLine);
  };

  addYAxis = ({
    name,
    opposite = false,
    visible = true,
    min = undefined,
    max = undefined,
    color = chartColors[0],
    plotLines = [],
  }) => {
    this.yAxis.push({
      labels: visible ? getYAxisLabel() : { enabled: false }, // label 숨기기
      title: {
        text: visible ? name : null, // 타이틀 숨기기
        style: {
          color: color ? color : chartColors[0],
        },
        margin: 1,
      },
      gridLineWidth: visible ? 1 : 0, // 그리드 숨기기
      lineWidth: 0, // 축선 숨기기
      tickWidth: 0, // 눈금 숨기기
      plotLines, // 여기는 그대로 둠
      opposite,
      color,
      min,
      max,
    });
  };

  addNumericYAxis = (
    name,
    opposite = false,
    visible = true,
    min = undefined,
    max = undefined,
    color = chartColors[0],
    plotLines = []
  ) => {
    this.addYAxis({
      name,
      opposite,
      visible,
      min,
      max,
      color,
      plotLines,
    });
  };

  addSidoColumnYValue = (index, dates, option) => {
    const opt = {
      ...option,
      name: option.name ? option.name : index.nameInfo.name,
      color: option.color
        ? option.color
        : getSidoColor(index.metadata.nameInfo.name),
      chartType: ChartType.COLUMN,
    };
    this.addYValue(index, dates, opt);
  };

  addSidoLineYValue = (index, dates, option) => {
    const opt = {
      ...option,
      name: option.name ? option.name : index.nameInfo.name,
      color: option.color
        ? option.color
        : getSidoColor(index.metadata.nameInfo.name),
      chartType: ChartType.LINE,
    };
    this.addYValue(index, dates, opt);
  };

  addSidoAreaYValue = (index, dates, option) => {
    const opt = {
      ...option,
      name: option.name ? option.name : index.nameInfo.name,
      color: option.color
        ? option.color
        : getSidoColor(index.metadata.nameInfo.name),
      chartType: ChartType.AREA,
    };
    this.addYValue(index, dates, opt);
  };

  addYValue = (index, dates, option) => {
    const {
      from = getMonthInLong(360),
      name,
      color,
      increase = false,
      empty = false,
      yAxis = 0,
      label = true,
      chartType = ChartType.LINE,
      dashStyle = DashType.SOLID,
      lineWidth = 2,
      lastLabel = false,
      dateType = DateTypes.MONTHLY,
      startValue = 100,
      connectNulls = true,
      showMarker = false,
      markerSize = 2,
      magnification = 1,
      markerSymbol = "circle",
      legend = true,
      stack = undefined,
      stacking = undefined,
      showValue = false,
      excludeFromSharedTooltip = false,
    } = option;
    let filtered = filterDatetimeData(
      index,
      dates,
      from,
      undefined,
      undefined,
      magnification
    );

    // 공통 날짜축 때문에 실제 데이터 시작 전 구간이 null로 채워져 있어도
    // Highcharts x축이 그 빈 기간부터 시작하지 않도록 선행 null만 제거한다.
    // 중간/후행 null은 실제 결측 구간 표현을 위해 그대로 유지한다.
    const firstValueIndex = filtered.findIndex((point) => point[1] != null);
    if (firstValueIndex === -1) {
      filtered = [];
    } else if (firstValueIndex > 0) {
      filtered = filtered.slice(firstValueIndex);
    }
    if (increase && filtered.length > 0) {
      let increased = [[filtered[0][0], startValue]]; // 첫 번째 인덱스를 시작값으로 설정
      let current = null;
      for (let i = 1; i < filtered.length; i++) {
        // 첫 번째 요소는 이미 추가했으므로 i = 1부터 시작
        current += filtered[i][1];
        increased.push([filtered[i][0], current]);
      }
      filtered = increased;
    }

    // region 이 필터되었을대, legend는 표시 하기 위해
    if (empty) {
      const nullData = [];
      for (const v of filtered) {
        nullData.push([v[0], null]);
      }
      filtered = nullData;
    }

    const seriesCountBefore = this.yValues.length;
    if (chartType === ChartType.LINE) {
      this.yValues.push(
        getLineValue(
          name,
          color,
          filtered,
          yAxis,
          label,
          dashStyle,
          lineWidth,
          connectNulls,
          showMarker,
          markerSize,
          legend,
          stack,
          stacking,
          showValue
        )
      );
    } else if (chartType === ChartType.COLUMN) {
      this.yValues.push(
        getColumnValue(
          name,
          color,
          filtered,
          dateType,
          yAxis,
          showValue || label,
          lastLabel && !showValue,
          legend,
          stack,
          stacking,
          showValue
        )
      );
    } else if (chartType === ChartType.AREA) {
      this.yValues.push(
        getAreaValue(
          name,
          color,
          filtered,
          dateType,
          yAxis,
          showValue || label,
          lastLabel && !showValue,
          legend,
          stack,
          stacking,
          showValue
        )
      );
    } else if (chartType === ChartType.SCATTER) {
      this.yValues.push(
        getScatterValue(
          name,
          color,
          filtered,
          yAxis,
          label,
          markerSize,
          markerSymbol,
          legend
        )
      );
    }

    // 개별 point hover tooltip은 유지하면서 공용 날짜 tooltip에서만 제외할 수 있다.
    const addedSeries = this.yValues.length > seriesCountBefore
      ? this.yValues[this.yValues.length - 1]
      : null;
    if (addedSeries) {
      addedSeries.excludeFromSharedTooltip = excludeFromSharedTooltip;
      // Highcharts merges updates: omitted fields retain the previous series label.
      addedSeries.custom = {
        ...addedSeries.custom,
        filterLabel: option.filterSubtitle
          ? formatChartFilterLabel(option.labelName ?? name, option.filterSubtitle)
          : null,
      };
      addedSeries.dataLabels = {
        ...addedSeries.dataLabels,
        useHTML: Boolean(option.filterSubtitle),
      };
    }
  };

  build = (option) => {
    const { xPoint = undefined, autoColor = false } = option ? option : {};
    const options = getChartLineOption({ ...option, height: this.height });
    if (this.yValues.some((series) => series.custom?.filterLabel)) {
      options.legend = {
        ...options.legend,
        useHTML: true,
        labelFormatter: function () {
          return this.options.custom?.filterLabel ?? formatChartFilterLabel(this.name);
        },
      };
    }
    options.xAxis = this.xAxis;
    if (xPoint) {
      options.xAxis.plotLines = [
        {
          value: xPoint,
          color: "red",
          width: 2,
          id: "plot-line-1",
          label: {
            text: "기준선",
            color: "red",
          },
        },
      ];
    } else {
      options.xAxis.plotLines = [];
    }
    if (autoColor) {
      var index = 0;
      for (var yValue of this.yValues) {
        var color = getSystemColor(index);
        yValue.color = color;
        yValue.dataLabels.color = color;
        yValue.dataLabels.style.textOutline = color;
        index += 1;
      }
    }
    options.yAxis = this.yAxis;
    options.series = this.yValues;
    options.title.text = this.chartName;
    return options;
  };
}

export const buildMultiIndexsBasicColumnChart = (
  indexs,
  dates,
  dateType,
  filter,
  sort = false,
  useDefaultColor = false
) => {
  const {
    from,
    gungu = false,
    title,
    tooltip,
    yAxisVisible = true,
    onMouseOver = null,
  } = filter;
  let data = [];
  if (indexs) {
    const { startIndex } = filterDates(dates, from, undefined, dateType);

    for (const index of indexs) {
      const indexData = index.data.slice(startIndex);
      let value = 0;
      for (const v of indexData) {
        value += v;
      }
      data.push([index, value]);
    }
  }

  if (sort) {
    data = data.slice().sort(sortBySecondItemDe);
  }

  const index = data;
  const categories = [];
  const series = [];
  const colors = []; // 시도별로 색상을 지정할 배열
  const ids = [];

  let colorIndex = 0;
  for (const i of index) {
    const nameInfo = i[0].metadata.nameInfo;
    if (!gungu) {
      categories.push(nameInfo.displayName || nameInfo.name);
    } else {
      categories.push(nameInfo.nameWithSidoName);
    }
    series.push(i[1]);
    ids.push(nameInfo.id);
    if (useDefaultColor) {
      colors.push(getSystemColor(colorIndex));
      colorIndex += 1;
    } else {
      if (!gungu) {
        colors.push(getSidoColor(nameInfo.name));
      } else {
        colors.push(getSidoColor(nameInfo.sidoName));
      }
    }
  }

  const xAxis = {
    categories,
    title: {
      text: null,
    },
    gridLineWidth: 1,
    lineWidth: 0,
  };

  const yAxis = {
    labels: {
      overflow: "justify",
    },
    gridLineWidth: 0,
    visible: yAxisVisible,
  };

  const options = getColumnOption(filter);
  options.xAxis = xAxis;
  options.yAxis = yAxis;
  options.series = [
    {
      name: title,
      data: series.map((value, index) => ({
        y: value,
        color: colors[index], // 해당 데이터 포인트의 색상을 지정
        customId: ids[index], // Attaching custom data
      })),
      dataLabels: {
        enabled: true,
        formatter: function () {
          return roundToTwoDecimal(this.y);
        },
      },
    },
  ];
  options.plotOptions.column.point = {
    events: {
      mouseOver: function (event) {
        const point = event.target; // 현재 마우스 오버된 컬럼 포인트
        onMouseOver?.(point.options.customId);
      },
    },
  };
  if (tooltip) {
    options.tooltip = tooltip;
  }
  return options;
};

export const buildMultiIndexsBasicBarChart = (indexs, filter) => {
  const { displayCount = 20, orderIn = true, gungu = false } = filter;
  const data = [];
  if (indexs) {
    for (const index of indexs) {
      let value = 0;
      for (const v of index.data) {
        value += v;
      }
      data.push([index, value]);
    }
  }

  let index = [];
  if (orderIn) {
    // 많이 내린순, 플러스 값들은 필터 필요.
    index = data.slice().sort(sortBySecondItemIn);
    index = index.map((item) => {
      // item[1]은 배열의 두 번째 요소를 참조합니다.
      // 이 값이 0보다 크면 null로 설정합니다.
      if (item[1] >= 0) {
        return [item[0], null];
      } else {
        return item;
      }
    });
  } else {
    // 많이 오른순, 마이너스 값은 필터 필요
    index = data.slice().sort(sortBySecondItemDe);
    index = index.map((item) => {
      // item[1]은 배열의 두 번째 요소를 참조합니다.
      // 이 값이 0보다 크면 null로 설정합니다.
      if (item[1] <= 0) {
        return [item[0], null];
      } else {
        return item;
      }
    });
  }
  if (index.length > displayCount) {
    index = index.slice(0, displayCount);
  }
  const categories = [];
  const series = [];
  const colors = []; // 시도별로 색상을 지정할 배열

  for (const i of index) {
    if (!gungu) {
      categories.push(i[0].metadata.nameInfo.name);
    } else {
      categories.push(i[0].metadata.nameInfo.nameWithSidoName);
    }
    series.push(i[1]);
    if (!gungu) {
      colors.push(getSidoColor(i[0].metadata.nameInfo.name));
    } else {
      colors.push(getSidoColor(i[0].metadata.nameInfo.sidoName));
    }
  }

  const xAxis = {
    categories,
    title: {
      text: null,
    },
    gridLineWidth: 1,
    lineWidth: 0,
  };

  const yAxis = {
    // min: 0,
    // title: {
    //   text: "지수증감율",
    //   align: "high",
    // },
    labels: {
      overflow: "justify",
    },
    gridLineWidth: 0,
  };

  const options = getBarOption(filter);
  options.xAxis = xAxis;
  options.yAxis = yAxis;
  options.series = [
    {
      name: "지수증감율",
      data: series.map((value, index) => ({
        y: value,
        color: colors[index], // 해당 데이터 포인트의 색상을 지정
      })),
      dataLabels: {
        enabled: true,
        formatter: function () {
          return roundToTwoDecimal(this.y);
        },
      },
    },
  ];
  return options;
};

export const buildRegionRankBarChart = (indexs, filter) => {
  const { title = "", regionKeys, topN = 10 } = filter;
  const data = [];
  if (indexs) {
    for (const index of indexs) {
      if (index.metadata.nameInfo.dataRange === "SR") continue;
      const key = `${index.metadata.nameInfo.sidoName}${index.metadata.nameInfo.name}`;
      if (regionKeys && !regionKeys.has(key)) continue;
      let value = 0;
      for (const v of index.data) {
        value += v;
      }
      data.push([index, value]);
    }
  }

  // 상승 TOP N (많이 오른 순)
  const upSorted = data
    .filter((item) => item[1] > 0)
    .sort(sortBySecondItemDe)
    .slice(0, topN);
  // 하락 TOP N (많이 내린 순)
  const downSorted = data
    .filter((item) => item[1] < 0)
    .sort(sortBySecondItemIn)
    .slice(0, topN);

  // 상승(위) + 하락(아래) 합치기
  const combined = [...upSorted, ...downSorted];

  const categories = [];
  const series = [];
  const colors = [];

  for (const i of combined) {
    const nameInfo = i[0].metadata.nameInfo;
    if (nameInfo.dataRange === "G") {
      categories.push(nameInfo.nameWithSidoName);
    } else {
      categories.push(nameInfo.name);
    }
    // 상승은 왼쪽(음수), 하락은 오른쪽(양수)으로 방향 반전
    series.push(-i[1]);
    colors.push(getSidoColor(nameInfo.sidoName || nameInfo.name));
  }

  const xAxis = {
    categories,
    title: { text: null },
    gridLineWidth: 1,
    lineWidth: 0,
  };

  const yAxis = {
    labels: {
      overflow: "justify",
      formatter: function () {
        return Math.abs(this.value);
      },
    },
    gridLineWidth: 0,
  };

  const options = getBarOption({ title });
  options.xAxis = xAxis;
  options.yAxis = yAxis;
  options.series = [
    {
      name: title,
      data: series.map((value, index) => ({
        y: value,
        color: colors[index],
      })),
      dataLabels: {
        enabled: true,
        formatter: function () {
          return roundToTwoDecimal(Math.abs(this.y));
        },
      },
    },
  ];
  return options;
};

export const buildMultiIndexsGeoMapChart = (indexs, filter) => {
  // 필터 다되어서 와야함, 그리고 증감이나 증감율만 봄
  const { gungu = false } = filter;

  const data = [];
  if (indexs) {
    for (const index of indexs) {
      let value = 0;
      for (const v of index.data) {
        value += v;
      }
      if (!gungu) {
        data.push([index.metadata.nameInfo.name, value]);
      } else {
        data.push([index.metadata.publicCode, value]);
      }
    }
  }

  const options = getGeoMapOption(filter);
  options.series[0].data = data;
  if (gungu) {
    options.series[0].keys = ["code", "value"];
    options.series[0].joinBy = ["code"];
  }
  return options;
};

export const buildMultiIndexsColumnChart = (indexs, dates, filter) => {
  const yValues = [];
  let dataName = "";

  const {
    selectedNames,
    from,
    legend = true,
    stack = false,
    label = false,
  } = filter;
  if (indexs && dates) {
    for (const index of indexs) {
      // selected name에 포함되어있을때 y value 표시
      const datetimeData = filterDatetimeData(
        index,
        dates,
        from,
        undefined,
        dates.dateType
      );
      // null 값을 넣으면 Y 값이 표시가 안되고 X 값음 표시됨
      if (isIndexInIds(index, selectedNames)) {
        yValues.push(
          getColumnValue(
            index.nameInfo.name,
            getSidoColor(index.nameInfo.sidoName),
            datetimeData,
            dates.dateType,
            0,
            label
          )
        );
      }
      dataName = index.dataName;
    }
  }

  // xAxis 갯수를 정함
  const xAxis = getDatetimeXaxis(dates.dateType);
  const yAxis = {
    // Primary yAxis
    labels: getYAxisLabel(),
    title: {
      text: "건수",
      style: {
        color: chartColors[0],
      },
    },
  };

  const options = getChartLineOption(legend, true, stack);
  options.xAxis.push(xAxis);
  options.yAxis.push(yAxis);
  options.series = yValues;
  options.title.text = dataName;
  return options;
};

export const buildMultiIndexsLineChart = (indexs, dates, dateType, filter) => {
  const yValues = [];
  let subName = "";

  const { from, isIncreaseRate, dataName = undefined } = filter;

  if (indexs && dates) {
    for (const index of indexs) {
      // selected name에 포함되어있을때 y value 표시
      let datetimeData = filterDatetimeData(
        index,
        dates,
        from,
        undefined,
        dateType
      );
      // null 값을 넣으면 Y 값이 표시가 안되고 X 값음 표시됨
      if (isIncreaseRate) {
        let increased = [];
        let current = null;
        for (const v of datetimeData) {
          if (current === null) {
            current = 100;
          } else {
            current += v[1];
          }
          increased.push([v[0], current]);
        }
        datetimeData = increased;
      }

      yValues.push({
        name: index.metadata.nameInfo.name,
        type: "spline",
        color: getSidoColor(index.metadata.nameInfo.name),
        // 종료된 인스부터 끝까지 가져오면 dates와 갯수가 동일해짐
        data: datetimeData,
        dataLabels: {
          enabled: true,
          allowOverlap: true,
          position: "right",
          borderColor: getSidoColor(index.metadata.nameInfo.name),
          x: -50,
          color: getSidoColor(index.metadata.nameInfo.name),
          // 마지막에만 Y label 보여주기 위해
          formatter: function () {
            // if last point
            if (this.point === this.series.data[this.series.data.length - 1]) {
              return this.series.name;
            }
          },
        },
      });
      subName = index.dataName;
    }
  }

  // xAxis 갯수를 정함
  const xAxis = getDatetimeXaxis(dates.dateType);
  const yAxis = {
    // Primary yAxis
    labels: {
      format: "{value}",
      style: {
        color: chartColors[0],
      },
    },
    title: {
      text: "지수",
      style: {
        color: chartColors[0],
      },
    },
  };

  const options = getChartLineOption(filter);
  options.xAxis.push(xAxis);
  options.yAxis.push(yAxis);
  options.series = yValues;
  options.title.text = dataName !== undefined ? dataName : subName;
  return options;
};

const genHighChartHeatMap = (filteredIndexs, dates, from) => {
  const { filtered, startIndex } = filterDates(dates.dates, from);
  const options = getHeatmapOption();
  const yAxis = {
    categories: filteredIndexs.map((i) => i.nameInfo.name),
    title: null,
    reversed: true,
  };
  // xAxis 갯수를 정함
  let xStep = parseInt(filtered.length / UIConfig.highchartXaxisCount);
  const xAxis = {
    categories: filtered.map((dateMils) => {
      var date = new Date(dateMils);

      // 로컬 시간을 고려하여 날짜를 포맷합니다.
      var formattedDate = new Intl.DateTimeFormat("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date);
      return formattedDate;
    }),
    labels: {
      step: xStep, // 레이블을 건너뛰는 간격 설정
      rotation: -45, // 기울임 각도 설정 (예: 45도)
      y: 30,
    },
  };
  const yDatas = filteredIndexs.map((f) => f.data.slice(startIndex));

  const data = [];
  // [date, sido, value]
  // const data = filtered.flatMap((_, dateIndex) => {
  //   return yDatas.flatMap((data, dataIndex) => {
  //     return data.map((value, _) => [dateIndex, dataIndex, value]);
  //   });
  // });

  for (let i = 0; i < filtered.length; i++) {
    for (let j = 0; j < yDatas.length; j++) {
      const yData = yDatas[j];
      // x, y, value 형식으로 저장됨
      data.push([i, j, yData[i]]);
    }
  }

  // console.log("## data: %O", data.slice(0, 1100));
  const heatMapHeight = yAxis.categories.length * 25 + 200;

  options.chart.height = heatMapHeight.toString();
  options.xAxis = xAxis;
  options.yAxis = yAxis;
  options.series = [
    {
      name: "시계열",
      // borderWidth: 1,
      // data: data.slice(0, 1100),
      data: data,
      dataLabels: {
        enabled: false,
        // color: "#000000",
      },
    },
  ];
  return options;
};

export const convertGunguPriceIndexChangedRateToHighChartHeatMap = (
  indexs,
  dates,
  filter
) => {
  const { selectedGungus, from } = filter;
  const filteredIndexs = filterGunguIndexs(indexs, selectedGungus, true);
  console.log("filtered : %O", filteredIndexs);
  return genHighChartHeatMap(filteredIndexs, dates, from);
};

export const convertPriceIndexChangedRateToHighChartHeatMap = (
  indexs,
  dates,
  filter
) => {
  const { selectedSidos, from } = filter;
  const filteredIndexs = filterIndexsById(indexs, selectedSidos);
  return genHighChartHeatMap(filteredIndexs, dates, from);
};

export const convertPriceIndexChangedRateToHighChartTileMap = (
  indexs,
  dates,
  filter
) => {
  const option = getTilemapOption();
  const { selectedNames, from } = filter;
  const { startIndex } = filterDates(dates.dates, from);

  const dataMap = new Map();
  indexs.forEach((index) => {
    // NOTE : index의 시도 이름이 selectedNames에 포함되거나 groupName이 포함되면 표시
    if (isIndexInIds(index, selectedNames)) {
      const indexData = index.data.slice(startIndex);
      let value = 0;
      for (const v of indexData) {
        value += v;
      }
      // NOTE: 경기도 광주시와 일반 광역시 가 이름이 동일해서 잘못 표기됨
      // sidoName과 name을 둘다 봐야됨
      const key = `${index.nameInfo.sidoName}${index.nameInfo.nameWithSidoName}`;
      dataMap.set(key, value);
    }
  });

  const regions = getRegionTilemapConfig();
  const data = [];
  regions.forEach((region) => {
    // NOTE: 경기도 광주시와 일반 광역시 가 이름이 동일해서 잘못 표기됨
    // sidoName과 name을 둘다 봐야됨
    const key = `${region.sidoName}${region.name}`;
    const value = dataMap.get(key);
    if (value) {
      data.push({
        name: region.name,
        x: region.x,
        y: region.y,
        value,
        // borderColor: getSidoColor(region.sidoName),
        dataLabels: {
          color: value > 0.16 || value < -0.16 ? "white" : "black", // 원하는 색상으로 변경하세요
          enabled: true,
        },
      });
    }
  });

  option.series = [
    {
      name: "",
      data,
      borderColor: "black",
      borderWidth: 0,
      // borderRadius: 3,
      pointPadding: 1,
      tileShape: "square",
      // tileShape: "hexagon",
      states: {
        hover: {
          borderWidth: 3,
          borderRadius: 3,
        },
      },
    },
  ];
  return option;
};
